import { FC, ReactNode, useEffect, useState } from "react";
import {
	Box,
	Button,
	Chip,
	IconButton,
	LinearProgress,
	MenuItem,
	OutlinedInput,
	Paper,
	Select,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useQuery } from "@tanstack/react-query";
import { useUserStore } from "../../../../../stores/user.store";
import {
	useCurrencySetting,
	useSettings,
} from "../../../../../hooks/useSetting";
import { useActivePayers } from "../../../../../hooks/userPayers";
import { useGroupLabels } from "../../../../../hooks/groupLabels";
import useVisitDetail from "../../../../../hooks/useVisitDetail";
import {
	VisitAnalyteResponse,
	VisitDetailFilters,
	reportsService,
} from "../../../../../services/reports.service";
import { triggerDownload } from "../../../../../utils/xlsx-export/build-workbook";
import { useAppTheme } from "../../../../../utils/theme";
import { HeadCell } from "../../../../../components/DataTable/dataTable.interface";
import {
	NoProgramCard,
	ProgramFilterField,
	ReportTabProps,
	programNameOf,
} from "../common";

interface Option {
	id: string;
	name: string | null;
}

const toOptions = (
	list: ReadonlyArray<{ _id?: string; id?: string; name?: string | null }>,
): Option[] =>
	(list ?? []).map((o) => ({ id: o._id ?? o.id ?? "", name: o.name ?? null }));

const HEAD: HeadCell[] = [
	{
		id: "visitDate",
		label: "Visit date",
		isSortable: false,
		disablePadding: false,
	},
	{ id: "member", label: "Member", isSortable: false, disablePadding: false },
	{ id: "cin", label: "CIN", isSortable: false, disablePadding: false },
	{
		id: "orgUnit",
		label: "Org unit",
		isSortable: false,
		disablePadding: false,
	},
	{
		id: "provider",
		label: "Provider",
		isSortable: false,
		disablePadding: false,
	},
	{ id: "eventName", label: "Event", isSortable: false, disablePadding: false },
	{ id: "result", label: "Result", isSortable: false, disablePadding: false },
	{
		id: "earned",
		label: "Earned",
		isSortable: false,
		disablePadding: false,
		align: "right",
	},
	{
		id: "payerPlanName",
		label: "Payer plan",
		isSortable: false,
		disablePadding: false,
	},
	{
		id: "details",
		label: "Actions",
		isSortable: false,
		disablePadding: false,
	},
];

interface SelectedVisit {
	enrollmentId: string;
	udtResultId: string;
	member: string | null;
	cin: string | null;
	eventName: string | null;
	visitDate: string | null;
	result: string | null;
	payerPlanName: string | null;
}

const VisitDetailTab: FC<ReportTabProps> = ({
	programId,
	programs,
	onProgramChange,
}) => {
	const theme = useAppTheme();
	const { userInfo } = useUserStore();
	const accountId = userInfo?.accountId ?? "";
	const currencyMask = useCurrencySetting()?.mask ?? "$";
	const programName = programNameOf(programs, programId);

	const [orgUnitIds, setOrgUnitIds] = useState<string[]>([]);
	const [payerIds, setPayerIds] = useState<string[]>([]);
	const [groupLabelIds, setGroupLabelIds] = useState<string[]>([]);
	const [providerId, setProviderId] = useState<string>("");
	const [startDate, setStartDate] = useState<string>("");
	const [endDate, setEndDate] = useState<string>("");
	const [cinInput, setCinInput] = useState<string>("");
	const [cin, setCin] = useState<string>("");
	const [pageNumber, setPageNumber] = useState<number>(0);
	const [pageSize] = useState<number>(25);
	const [selected, setSelected] = useState<SelectedVisit | null>(null);
	// Which analyte row is highlighted within the drill-down — drives the
	// analyte fields shown in the detail card. Resets when the visit changes.
	const [analyteIdx, setAnalyteIdx] = useState<number | null>(null);

	// Brand logo comes from Settings → brand information; SIMS product logo is a
	// static asset. Generated date stamps the report (matches the B1 handoff).
	const { data: settings } = useSettings();
	const brand = settings?.brandInformation;
	const generated = dayjs().format("MM/DD/YYYY");

	// Design tokens → theme palette (driven by the account's themeColors).
	const c = {
		cardBg: theme.palette.surface.light,
		band: theme.palette.surface.main,
		border: theme.palette.outline.light,
		divider: theme.palette.outline.light,
		label: theme.palette.textSecondary.main,
		muted: theme.palette.textSecondary.main,
		ink: theme.palette.textPrimary.main,
		accent: theme.palette.primary.main,
		accentBand: theme.palette.surface.dark,
		bandDark: theme.palette.primary.dark, // --sims-purple-900 (title band)
		onDark: theme.palette.surface.light, // text on the band
		teal: theme.palette.secondary.main, // --sims-teal-500 (section rule)
		posBg: theme.palette.error.light, // highlighted POSITIVE analyte row
		posInk: theme.palette.error.dark,
		negInk: theme.palette.secondary.dark,
	};

	const hexToRgb = (hex: string): [number, number, number] => {
		const h = (hex || "").replace("#", "");
		const s =
			h.length === 3
				? h
						.split("")
						.map((x) => x + x)
						.join("")
				: h;
		const n = parseInt(s || "000000", 16);
		return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
	};

	const { data, isLoading, isFetching } = useVisitDetail({
		accountId,
		programId,
		orgUnitIds: orgUnitIds.length ? orgUnitIds.join(",") : undefined,
		payerIds: payerIds.length ? payerIds.join(",") : undefined,
		groupLabelIds: groupLabelIds.length ? groupLabelIds.join(",") : undefined,
		providerId: providerId || undefined,
		startDate: startDate || undefined,
		endDate: endDate || undefined,
		cin: cin || undefined,
		pageNumber,
		pageSize,
	});

	// Hide the analyte drill-down when the expanded visit leaves the results.
	useEffect(() => {
		if (
			selected &&
			!(data?.rows ?? []).some((r) => r.udtResultId === selected.udtResultId)
		) {
			setSelected(null);
		}
	}, [data, selected]);

	// Clear the analyte selection whenever the visit changes (card falls back to
	// the visit-level result until a new analyte row is clicked).
	useEffect(() => {
		setAnalyteIdx(null);
	}, [selected?.udtResultId]);

	const { data: payers } = useActivePayers();
	const { data: groupLabels } = useGroupLabels();
	const payerOptions = toOptions(payers ?? []);
	const groupLabelOptions = toOptions(groupLabels ?? []);
	const siteOptions = toOptions(data?.availableSites ?? []);
	const providerOptions = toOptions(data?.availableProviders ?? []);

	const money = (n: number): string => `${currencyMask}${(n ?? 0).toFixed(2)}`;

	const nameById = (opts: Option[], ids: string[]): string =>
		ids
			.map((id) => opts.find((o) => o.id === id)?.name ?? id)
			.filter(Boolean)
			.join(", ");

	const contextLine = (): string => {
		const dateRange =
			startDate || endDate
				? `${startDate ? dayjs(startDate).format("MM/DD/YYYY") : "…"} – ${
						endDate ? dayjs(endDate).format("MM/DD/YYYY") : "…"
					}`
				: "";
		return [
			programName,
			nameById(siteOptions, orgUnitIds),
			nameById(payerOptions, payerIds),
			nameById(groupLabelOptions, groupLabelIds),
			dateRange,
		]
			.filter((s) => s && s.length > 0)
			.join(" · ");
	};

	// All filters by name, with values (or "All"/"Any" when unselected) — shown
	// in the report header so the PDF records exactly what was applied.
	const filterPairs = (): Array<[string, string]> => [
		["Program", programName || "All"],
		["Org units", nameById(siteOptions, orgUnitIds) || "All"],
		["Payer", nameById(payerOptions, payerIds) || "All"],
		["Group labels", nameById(groupLabelOptions, groupLabelIds) || "All"],
		[
			"Provider",
			providerOptions.find((o) => o.id === providerId)?.name || "All",
		],
		["Start date", startDate ? dayjs(startDate).format("MM/DD/YYYY") : "Any"],
		["End date", endDate ? dayjs(endDate).format("MM/DD/YYYY") : "Any"],
		["CIN", cin || "All"],
	];

	// Result chip styling by semantic type, all from theme tokens.
	const resultStyle = (value: string | null): { bg: string; color: string } => {
		const s = (value ?? "").toLowerCase();
		if (/(unexcused|positive|missed|absent)/.test(s)) {
			return { bg: theme.palette.error.light, color: theme.palette.error.dark };
		}
		if (/excus/.test(s)) {
			return {
				bg: theme.palette.warning.light,
				color: theme.palette.warning.dark,
			};
		}
		if (/(negative|attend)/.test(s)) {
			return {
				bg: theme.palette.secondary.light,
				color: theme.palette.secondary.dark,
			};
		}
		return { bg: c.band, color: c.muted };
	};

	const ResultPill: FC<{ value: string | null }> = ({ value }) => {
		if (!value) return <span>—</span>;
		const st = resultStyle(value);
		return (
			<Box
				component="span"
				sx={{
					display: "inline-block",
					bgcolor: st.bg,
					color: st.color,
					fontSize: 13,
					fontWeight: 600,
					px: 1.5,
					py: 0.5,
					borderRadius: "9999px",
				}}
			>
				{value}
			</Box>
		);
	};

	const visitRows = data?.rows ?? [];
	const earnedTotal = visitRows.reduce((sum, r) => sum + (r.earned ?? 0), 0);

	// Select a visit row → drives the two detail cards (meta + analytes).
	const selectVisit = (r: (typeof visitRows)[number]): void => {
		if (!r.hasAnalyteDetails) return;
		setSelected(
			selected?.udtResultId === r.udtResultId
				? null
				: {
						enrollmentId: r.enrollmentId,
						udtResultId: r.udtResultId,
						member: r.member,
						cin: r.cin,
						eventName: r.eventName,
						visitDate: r.visitDate,
						result: r.result,
						payerPlanName: r.payerPlanName,
					},
		);
	};

	// Lazy analyte drill-down for the expanded visit.
	const analyteQuery = useQuery<VisitAnalyteResponse>({
		queryKey: [
			"reports-visit-analytes",
			selected?.enrollmentId ?? "",
			selected?.udtResultId ?? "",
		],
		queryFn: () =>
			selected
				? reportsService.getVisitAnalytes(
						accountId,
						selected.enrollmentId,
						selected.udtResultId,
					)
				: Promise.reject(new Error("no visit selected")),
		enabled: Boolean(selected),
		staleTime: 1000 * 60 * 5,
	});

	// Analyte rows for the drill-down table. Clicking a row selects it and the
	// card's "Visit result" then reflects that analyte (falls back to the visit
	// result when nothing is selected).
	const analytes = analyteQuery.data?.analytes ?? [];
	const selAnalyte = analyteIdx !== null ? analytes[analyteIdx] : undefined;

	const exportFilters = (): VisitDetailFilters => ({
		accountId,
		programId,
		orgUnitIds: orgUnitIds.length ? orgUnitIds.join(",") : undefined,
		payerIds: payerIds.length ? payerIds.join(",") : undefined,
		groupLabelIds: groupLabelIds.length ? groupLabelIds.join(",") : undefined,
		providerId: providerId || undefined,
		startDate: startDate || undefined,
		endDate: endDate || undefined,
		cin: cin || undefined,
		filterSummary: contextLine(),
	});

	const [exporting, setExporting] = useState<"" | "excel" | "pdf">("");

	const handleExcel = async (): Promise<void> => {
		setExporting("excel");
		try {
			const blob = await reportsService.getVisitDetailExcel(exportFilters());
			triggerDownload({ blob, filename: "visit_detail.xlsx" });
		} finally {
			setExporting("");
		}
	};

	// Redesign (B1) — the PDF follows the same pattern as the screen: purple
	// title band, teal section rule, themed table with colored result cells, and
	// a SIMS footer with the generated date. Colors come from the theme tokens.
	const handlePdf = async (): Promise<void> => {
		setExporting("pdf");
		try {
			const allRows = await reportsService.fetchAllVisitDetail(exportFilters());
			const doc = new jsPDF("landscape", "pt", "a4");
			const pageW = doc.internal.pageSize.getWidth();
			const pageH = doc.internal.pageSize.getHeight();
			const M = 40;
			const purple = hexToRgb(theme.palette.primary.dark);
			const teal = hexToRgb(theme.palette.secondary.main);
			const muted = hexToRgb(theme.palette.textSecondary.main);
			const headBg = hexToRgb(theme.palette.surface.main);
			const posInk = hexToRgb(theme.palette.error.dark);
			const negInk = hexToRgb(theme.palette.secondary.dark);

			// Title band
			doc.setFillColor(purple[0], purple[1], purple[2]);
			doc.roundedRect(M, 34, pageW - M * 2, 44, 5, 5, "F");
			doc.setTextColor(255, 255, 255);
			doc.setFont("helvetica", "bold");
			doc.setFontSize(16);
			doc.text("Visit Detail Report", M + 16, 62);
			doc.setFont("helvetica", "normal");
			doc.setFontSize(9);
			doc.text(`Generated ${generated}`, pageW - M - 16, 62, {
				align: "right",
			});

			// Applied filters — every filter by name, "All"/"Any" when unset.
			let y = 96;
			doc.setFontSize(8);
			const filterLine = filterPairs()
				.map(([k, v]) => `${k}: ${v}`)
				.join("    ");
			const wrapped = doc.splitTextToSize(filterLine, pageW - M * 2);
			doc.setTextColor(muted[0], muted[1], muted[2]);
			doc.text(wrapped, M, y);
			y += wrapped.length * 11 + 6;

			// "Visits" section rule
			doc.setTextColor(purple[0], purple[1], purple[2]);
			doc.setFont("helvetica", "bold");
			doc.setFontSize(12);
			doc.text("Visits", M, y + 4);
			doc.setFont("helvetica", "normal");
			doc.setFontSize(8.5);
			doc.setTextColor(muted[0], muted[1], muted[2]);
			doc.text(`${allRows.length} rows`, pageW - M, y + 4, { align: "right" });
			doc.setDrawColor(teal[0], teal[1], teal[2]);
			doc.setLineWidth(1.5);
			doc.line(M, y + 9, pageW - M, y + 9);
			y += 20;

			autoTable(doc, {
				startY: y,
				theme: "grid",
				margin: { left: M, right: M },
				styles: { fontSize: 8 },
				headStyles: { fillColor: headBg, textColor: purple, fontStyle: "bold" },
				columnStyles: { 7: { halign: "right" } },
				head: [
					[
						"Visit date",
						"Member",
						"CIN",
						"Org unit",
						"Provider",
						"Event",
						"Result",
						"Earned",
						"Payer plan",
					],
				],
				body: allRows.map((r) => [
					r.visitDate ? dayjs(r.visitDate).format("MM/DD/YYYY") : "—",
					r.member ?? "—",
					r.cin ?? "—",
					r.orgUnit ?? "—",
					r.provider ?? "—",
					r.eventName ?? "—",
					r.result ?? "—",
					money(r.earned),
					r.payerPlanName ?? "—",
				]),
				didParseCell: (hook) => {
					if (hook.section === "body" && hook.column.index === 6) {
						const v = (allRows[hook.row.index]?.result ?? "").toLowerCase();
						if (/(unexcused|positive|missed|absent)/.test(v)) {
							hook.cell.styles.textColor = posInk;
							hook.cell.styles.fontStyle = "bold";
						} else if (/(negative|attend)/.test(v)) {
							hook.cell.styles.textColor = negInk;
							hook.cell.styles.fontStyle = "bold";
						}
					}
				},
			});

			// Footer on every page
			const brandLeft = [programName, brand?.title].filter(Boolean).join(" — ");
			const pages = doc.internal.pages.length - 1;
			for (let p = 1; p <= pages; p++) {
				doc.setPage(p);
				doc.setDrawColor(muted[0], muted[1], muted[2]);
				doc.setLineWidth(0.5);
				doc.line(M, pageH - 30, pageW - M, pageH - 30);
				doc.setFontSize(8);
				doc.setFont("helvetica", "normal");
				doc.setTextColor(muted[0], muted[1], muted[2]);
				doc.text(brandLeft, M, pageH - 18);
				doc.setFont("helvetica", "bold");
				doc.setTextColor(purple[0], purple[1], purple[2]);
				doc.text("SIMS", pageW / 2, pageH - 18, { align: "center" });
				doc.setFont("helvetica", "normal");
				doc.setTextColor(muted[0], muted[1], muted[2]);
				doc.text(`Page ${p} of ${pages}`, pageW - M, pageH - 18, {
					align: "right",
				});
			}

			doc.save("visit_detail.pdf");
		} finally {
			setExporting("");
		}
	};

	const applyCin = (): void => {
		if (cinInput.trim() !== cin) {
			setCin(cinInput.trim());
			setPageNumber(0);
		}
	};

	const total = data?.pagination.total ?? 0;
	const rangeStart = total === 0 ? 0 : pageNumber * pageSize + 1;
	const rangeEnd = Math.min((pageNumber + 1) * pageSize, total);

	// ---- presentational helpers -------------------------------------------
	const FilterField: FC<{
		label: string;
		badge?: string;
		children: ReactNode;
	}> = ({ label, badge, children }) => (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
			<Stack direction="row" spacing={1} alignItems="center">
				<Typography
					sx={{
						fontSize: 12,
						fontWeight: 600,
						letterSpacing: "0.06em",
						color: c.label,
					}}
				>
					{label.toUpperCase()}
				</Typography>
				{badge && (
					<Chip
						size="small"
						label={badge}
						sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
						color="primary"
						variant="outlined"
					/>
				)}
			</Stack>
			{children}
		</Box>
	);

	const multiInput = (
		value: string[],
		options: Option[],
		onChange: (v: string[]) => void,
		placeholder: string,
	): ReactNode => (
		<Select
			size="small"
			multiple
			displayEmpty
			value={value}
			input={<OutlinedInput />}
			onChange={(e) => {
				const v = e.target.value;
				onChange(typeof v === "string" ? v.split(",") : v);
				setPageNumber(0);
			}}
			renderValue={(sel) =>
				(sel as string[]).length === 0 ? (
					<Typography sx={{ color: c.muted }}>{placeholder}</Typography>
				) : (
					<Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
						{(sel as string[]).map((id) => (
							<Chip
								key={id}
								size="small"
								label={options.find((o) => o.id === id)?.name ?? id}
							/>
						))}
					</Box>
				)
			}
		>
			{options.map((o) => (
				<MenuItem key={o.id} value={o.id}>
					{o.name ?? o.id}
				</MenuItem>
			))}
		</Select>
	);

	const summaryField = (label: string, value: ReactNode): ReactNode => (
		<Box>
			<Typography sx={{ fontSize: 14, color: c.muted, mb: 0.5 }}>
				{label}
			</Typography>
			<Box sx={{ fontSize: 17, color: c.ink }}>{value}</Box>
		</Box>
	);

	const clearFilters = (): void => {
		// SIMS-7390: a program is always selected — reset to the first assigned
		// program rather than an empty selection.
		onProgramChange(programs[0]?._id ?? "");
		setOrgUnitIds([]);
		setPayerIds([]);
		setGroupLabelIds([]);
		setProviderId("");
		setStartDate("");
		setEndDate("");
		setCinInput("");
		setCin("");
		setPageNumber(0);
		setSelected(null);
	};

	// ---- report chrome (B1) — theme tokens + brand logo from settings -------
	const SectionHead: FC<{ title: string; sub?: string }> = ({ title, sub }) => (
		<Box
			sx={{
				display: "flex",
				alignItems: "baseline",
				justifyContent: "space-between",
				borderBottom: `2px solid ${c.teal}`,
				pb: 0.75,
				mb: 2,
				gap: 2,
			}}
		>
			<Typography sx={{ fontSize: 16, fontWeight: 700, color: c.bandDark }}>
				{title}
			</Typography>
			{sub && (
				<Typography sx={{ fontSize: 12, color: c.muted, whiteSpace: "nowrap" }}>
					{sub}
				</Typography>
			)}
		</Box>
	);

	const ReportHeader: FC = () => (
		<Box>
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				sx={{ mb: 2 }}
			>
				{brand?.logo ? (
					<Box
						component="img"
						src={brand.logo}
						alt={brand.title ?? "Brand"}
						sx={{ height: 40, maxWidth: 220, objectFit: "contain" }}
					/>
				) : (
					<Typography sx={{ fontSize: 18, fontWeight: 700, color: c.ink }}>
						{brand?.title ?? ""}
					</Typography>
				)}
				<Box
					component="img"
					src="/assets/sims-logo.png"
					alt="SIMS"
					sx={{ height: 44, objectFit: "contain" }}
				/>
			</Stack>
			<Box
				sx={{
					bgcolor: c.bandDark,
					borderRadius: 2,
					px: 3,
					py: 2,
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 2,
					flexWrap: "wrap",
				}}
			>
				<Typography sx={{ color: c.onDark, fontSize: 22, fontWeight: 700 }}>
					Visit Detail Report
				</Typography>
				<Stack direction="row" spacing={1.5} alignItems="center">
					<Typography sx={{ color: c.onDark, opacity: 0.75, fontSize: 12 }}>
						Generated {generated}
					</Typography>
					<Button
						variant="contained"
						color="secondary"
						size="small"
						startIcon={<FileDownloadOutlinedIcon />}
						disabled={exporting !== ""}
						onClick={handleExcel}
					>
						{exporting === "excel" ? "Exporting…" : "Excel"}
					</Button>
					<Button
						variant="contained"
						color="secondary"
						size="small"
						startIcon={<FileDownloadOutlinedIcon />}
						disabled={exporting !== ""}
						onClick={handlePdf}
					>
						{exporting === "pdf" ? "Exporting…" : "PDF"}
					</Button>
				</Stack>
			</Box>
			<Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 1.5 }}>
				{filterPairs().map(([k, v]) => (
					<Typography key={k} sx={{ fontSize: 11, color: c.muted }}>
						{k}:{" "}
						<Box component="span" sx={{ color: c.ink, fontWeight: 600 }}>
							{v}
						</Box>
					</Typography>
				))}
			</Box>
		</Box>
	);

	const Footnote: FC = () => (
		<Typography sx={{ fontSize: 11, color: c.muted, mt: 1.5, lineHeight: 1.5 }}>
			Each analyte generates a separate row in the Excel export, with visit
			fields repeated per analyte (Visit date, Member, CIN, Event, Result,
			Earned, Payer plan, Cup, Tampered, Short name, Scientific name, Analyte
			result). Visits without cup analytes export a single row.
		</Typography>
	);

	return (
		<Stack spacing={3}>
			{/* Report header: brand logo + SIMS, purple title band, context line */}
			<Paper
				variant="outlined"
				sx={{ borderColor: c.border, borderRadius: 3, p: 3 }}
			>
				<ReportHeader />
			</Paper>

			{/* Filter bar card */}
			<Paper
				variant="outlined"
				sx={{ borderColor: c.border, borderRadius: 3, p: 3 }}
			>
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: {
							xs: "1fr",
							sm: "repeat(2,1fr)",
							md: "repeat(4,1fr) 112px",
						},
						gap: 3,
						alignItems: "end",
					}}
				>
					<ProgramFilterField
						programId={programId}
						programs={programs}
						onProgramChange={(id) => {
							onProgramChange(id);
							setPageNumber(0);
							setSelected(null);
						}}
						labelColor={c.label}
						mutedColor={c.muted}
						errorColor={theme.palette.error.main}
					/>
					<FilterField label="Org units">
						{multiInput(orgUnitIds, siteOptions, setOrgUnitIds, "All sites")}
					</FilterField>
					<FilterField label="Payer">
						{multiInput(payerIds, payerOptions, setPayerIds, "All payers")}
					</FilterField>
					<FilterField label="Group labels">
						{multiInput(
							groupLabelIds,
							groupLabelOptions,
							setGroupLabelIds,
							"All",
						)}
					</FilterField>
					<FilterField label="Provider">
						<Select
							size="small"
							displayEmpty
							value={providerId}
							input={<OutlinedInput />}
							onChange={(e) => {
								setProviderId(e.target.value);
								setPageNumber(0);
							}}
							renderValue={(v) =>
								v ? (
									(providerOptions.find((o) => o.id === v)?.name ?? v)
								) : (
									<Typography sx={{ color: c.muted }}>All providers</Typography>
								)
							}
						>
							<MenuItem value="">All providers</MenuItem>
							{providerOptions.map((o) => (
								<MenuItem key={o.id} value={o.id}>
									{o.name ?? o.id}
								</MenuItem>
							))}
						</Select>
					</FilterField>
					<FilterField label="Start date">
						<TextField
							size="small"
							type="date"
							value={startDate}
							onChange={(e) => {
								setStartDate(e.target.value);
								setPageNumber(0);
							}}
							InputLabelProps={{ shrink: true }}
						/>
					</FilterField>
					<FilterField label="End date">
						<TextField
							size="small"
							type="date"
							value={endDate}
							onChange={(e) => {
								setEndDate(e.target.value);
								setPageNumber(0);
							}}
							InputLabelProps={{ shrink: true }}
						/>
					</FilterField>
					<FilterField label="CIN" badge="NEW">
						<TextField
							size="small"
							placeholder="Search by CIN"
							value={cinInput}
							onChange={(e) => setCinInput(e.target.value)}
							onBlur={applyCin}
							onKeyDown={(e) => {
								if (e.key === "Enter") applyCin();
							}}
						/>
					</FilterField>
					{/* Clear all — 5th column spanning both filter rows (per handoff) */}
					<Button
						variant="contained"
						onClick={clearFilters}
						sx={{
							gridColumn: { xs: "1 / -1", md: "5" },
							gridRow: { md: "1 / span 2" },
							alignSelf: "stretch",
							minHeight: 44,
							bgcolor: c.bandDark,
							color: c.onDark,
							fontWeight: 700,
							letterSpacing: "0.02em",
							"&:hover": { bgcolor: c.bandDark, opacity: 0.92 },
						}}
					>
						Clear all
					</Button>
				</Box>

				<Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 3 }}>
					<Box
						sx={{
							width: 8,
							height: 8,
							borderRadius: "9999px",
							bgcolor: theme.palette.secondary.main,
						}}
					/>
					<Typography sx={{ fontSize: 15, color: c.muted }}>
						Report refreshes automatically on filter change
					</Typography>
				</Stack>
			</Paper>

			{/* Reserve the bar's height so the table doesn't jump on refetch. */}
			<Box sx={{ height: 4 }}>
				{(isLoading || isFetching) && <LinearProgress />}
			</Box>

			{!programId && (
				<NoProgramCard borderColor={c.border} mutedColor={c.muted} />
			)}

			{programId && (
				<>
					{/* Visit results card */}
					<Paper
						variant="outlined"
						sx={{
							borderColor: c.border,
							borderRadius: 3,
							overflow: "hidden",
							minWidth: 0,
						}}
					>
						<Box sx={{ px: 3, pt: 2.5 }}>
							<SectionHead
								title="Visits"
								sub={`${rangeStart}–${rangeEnd} of ${total} visits`}
							/>
						</Box>

						{/* Plain table (B1 handoff): no selection — a "See details"
						    action drives the drill-down. Rows are clickable to open
						    the two detail cards; a totals row sums Earned. */}
						<Box sx={{ px: 3, pb: 1, overflowX: "auto" }}>
							<Box
								component="table"
								sx={{
									width: "100%",
									minWidth: 900,
									borderCollapse: "collapse",
									fontSize: 14,
								}}
							>
								<Box component="thead">
									<Box
										component="tr"
										sx={{ borderBottom: `2px solid ${c.divider}` }}
									>
										{HEAD.map((h) => (
											<Box
												key={h.id}
												component="th"
												sx={{
													p: "10px 12px",
													fontSize: 12,
													fontWeight: 700,
													letterSpacing: "0.04em",
													textTransform: "uppercase",
													whiteSpace: "nowrap",
													color: c.muted,
													textAlign: h.align === "right" ? "right" : "left",
												}}
											>
												{h.label}
											</Box>
										))}
									</Box>
								</Box>
								<Box component="tbody">
									{isLoading || isFetching ? (
										<Box component="tr">
											<Box component="td" colSpan={HEAD.length} sx={{ p: 3 }}>
												<LinearProgress />
											</Box>
										</Box>
									) : visitRows.length === 0 ? (
										<Box component="tr">
											<Box
												component="td"
												colSpan={HEAD.length}
												sx={{ p: 4, textAlign: "center", color: c.muted }}
											>
												No visits match the current filters.
											</Box>
										</Box>
									) : (
										<>
											{visitRows.map((r) => {
												const isOpen = selected?.udtResultId === r.udtResultId;
												const clickable = r.hasAnalyteDetails;
												return (
													<Box
														key={r.udtResultId}
														component="tr"
														onClick={() => selectVisit(r)}
														sx={{
															borderBottom: `1px solid ${c.divider}`,
															bgcolor: isOpen ? c.band : "transparent",
															cursor: clickable ? "pointer" : "default",
															"&:hover": clickable
																? { bgcolor: c.band }
																: undefined,
														}}
													>
														<Box
															component="td"
															sx={{ p: "10px 12px", whiteSpace: "nowrap" }}
														>
															{r.visitDate
																? dayjs(r.visitDate).format("MM/DD/YYYY")
																: "—"}
														</Box>
														<Box component="td" sx={{ p: "10px 12px" }}>
															{r.member ?? "—"}
														</Box>
														<Box
															component="td"
															sx={{ p: "10px 12px", whiteSpace: "nowrap" }}
														>
															{r.cin ?? "—"}
														</Box>
														<Box component="td" sx={{ p: "10px 12px" }}>
															{r.orgUnit ?? "—"}
														</Box>
														<Box component="td" sx={{ p: "10px 12px" }}>
															{r.provider ?? "—"}
														</Box>
														<Box component="td" sx={{ p: "10px 12px" }}>
															{r.eventName ?? "—"}
														</Box>
														<Box component="td" sx={{ p: "10px 12px" }}>
															<ResultPill value={r.result} />
														</Box>
														<Box
															component="td"
															sx={{
																p: "10px 12px",
																textAlign: "right",
																whiteSpace: "nowrap",
															}}
														>
															{money(r.earned)}
														</Box>
														<Box
															component="td"
															sx={{ p: "10px 12px", whiteSpace: "nowrap" }}
														>
															{r.payerPlanName ?? "—"}
														</Box>
														<Box
															component="td"
															sx={{ p: "10px 12px", whiteSpace: "nowrap" }}
														>
															{r.hasAnalyteDetails ? (
																<Box
																	component="span"
																	sx={{
																		color: c.accent,
																		textDecoration: "underline",
																		fontWeight: isOpen ? 700 : 500,
																	}}
																>
																	{isOpen ? "Hide details" : "See details"}
																</Box>
															) : (
																<Typography
																	component="span"
																	variant="caption"
																	color="text.secondary"
																>
																	—
																</Typography>
															)}
														</Box>
													</Box>
												);
											})}
											{/* Totals row — sums the Earned column for this page. */}
											<Box
												component="tr"
												sx={{
													bgcolor: c.band,
													borderTop: `2px solid ${c.divider}`,
												}}
											>
												<Box
													component="td"
													colSpan={7}
													sx={{
														p: "10px 12px",
														fontWeight: 700,
														color: c.bandDark,
													}}
												>
													{`Totals — ${visitRows.length} ${
														visitRows.length === 1 ? "visit" : "visits"
													}`}
												</Box>
												<Box
													component="td"
													sx={{
														p: "10px 12px",
														textAlign: "right",
														fontWeight: 700,
														color: c.bandDark,
													}}
												>
													{money(earnedTotal)}
												</Box>
												<Box component="td" colSpan={2} />
											</Box>
										</>
									)}
								</Box>
							</Box>
						</Box>

						{/* Pager — mirrors EnrollmentSummary (count + prev/next). */}
						<Stack
							direction="row"
							alignItems="center"
							justifyContent="space-between"
							sx={{
								px: 3,
								py: 1.5,
								borderTop: `1px solid ${c.divider}`,
							}}
						>
							<Typography sx={{ fontSize: 14, color: c.muted }}>
								{`${rangeStart}–${rangeEnd} of ${total} visits`}
							</Typography>
							<Stack direction="row" spacing={1} alignItems="center">
								<Button
									size="small"
									variant="outlined"
									disabled={pageNumber === 0}
									onClick={() => setPageNumber((p) => Math.max(0, p - 1))}
									sx={{ color: c.ink, borderColor: c.border }}
								>
									Prev
								</Button>
								<Typography sx={{ fontSize: 14, color: c.muted }}>
									{`Page ${pageNumber + 1} of ${Math.max(
										1,
										Math.ceil(total / pageSize),
									)}`}
								</Typography>
								<Button
									size="small"
									variant="outlined"
									disabled={rangeEnd >= total}
									onClick={() => setPageNumber((p) => p + 1)}
									sx={{ color: c.ink, borderColor: c.border }}
								>
									Next
								</Button>
							</Stack>
						</Stack>
					</Paper>

					{/* Analyte detail panel */}
					{selected && (
						<Box>
							{/* Analyte details — teal section + two cards (meta + results) */}
							<Box
								sx={{
									display: "flex",
									alignItems: "baseline",
									justifyContent: "space-between",
									borderBottom: `2px solid ${c.teal}`,
									pb: 0.75,
									mb: 2,
									gap: 2,
								}}
							>
								<Box>
									<Typography
										sx={{ fontSize: 16, fontWeight: 700, color: c.bandDark }}
									>
										Analyte details
									</Typography>
									<Typography sx={{ fontSize: 12, color: c.muted, mt: 0.25 }}>
										{[
											selected.eventName,
											selected.visitDate
												? dayjs(selected.visitDate).format("MM/DD/YYYY")
												: null,
										]
											.filter(Boolean)
											.join(" · ")}
									</Typography>
								</Box>
								<IconButton
									size="small"
									onClick={() => setSelected(null)}
									aria-label="Close analyte details"
								>
									<CloseIcon fontSize="small" />
								</IconButton>
							</Box>

							{analyteQuery.isLoading ? (
								<LinearProgress />
							) : (
								<Stack spacing={2}>
									{/* Card 1 — visit meta */}
									<Paper
										variant="outlined"
										sx={{ borderColor: c.border, borderRadius: 3, p: 2.5 }}
									>
										<Box sx={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
											{summaryField("Member", selected.member ?? "—")}
											{summaryField("CIN", selected.cin ?? "—")}
											{summaryField("Cup", analyteQuery.data?.cupName ?? "—")}
											{summaryField(
												"Tampered",
												analyteQuery.data?.isTampered ? (
													<Chip
														size="small"
														color="warning"
														label={
															analyteQuery.data.tamperedReasons.length
																? `Yes — ${analyteQuery.data.tamperedReasons.join(", ")}`
																: "Yes"
														}
													/>
												) : (
													<Chip
														size="small"
														label="No"
														sx={{
															bgcolor: theme.palette.secondary.light,
															color: theme.palette.secondary.dark,
														}}
													/>
												),
											)}
											{summaryField(
												"Payer plan",
												selected.payerPlanName ?? "—",
											)}
											{summaryField(
												"Visit result",
												<ResultPill
													value={
														selAnalyte
															? selAnalyte.positive
																? "Positive"
																: "Negative"
															: selected.result
													}
												/>,
											)}
										</Box>
									</Paper>

									{/* Card 2 — analyte results */}
									<Paper
										variant="outlined"
										sx={{
											borderColor: c.border,
											borderRadius: 3,
											overflow: "hidden",
										}}
									>
										<Box sx={{ overflowX: "auto" }}>
											<Box
												component="table"
												sx={{ width: "100%", borderCollapse: "collapse" }}
											>
												<Box component="thead">
													<Box
														component="tr"
														sx={{
															textAlign: "left",
															color: c.muted,
															borderBottom: `1px solid ${c.divider}`,
														}}
													>
														{[
															"Short name",
															"Scientific name",
															"Analyte result",
														].map((h, i) => (
															<Box
																key={h}
																component="th"
																sx={{
																	fontSize: 12,
																	fontWeight: 600,
																	letterSpacing: "0.05em",
																	p: "14px 24px",
																	textAlign: i === 2 ? "right" : "left",
																	textTransform: "uppercase",
																}}
															>
																{h}
															</Box>
														))}
													</Box>
												</Box>
												<Box component="tbody">
													{analytes.length === 0 ? (
														<Box component="tr">
															<Box
																component="td"
																colSpan={3}
																sx={{ p: "18px 24px", color: c.muted }}
															>
																No analytes recorded for this visit.
															</Box>
														</Box>
													) : (
														analytes.map((a, i) => (
															<Box
																component="tr"
																key={`${a.shortName ?? "analyte"}-${i}`}
																onClick={() => setAnalyteIdx(i)}
																sx={{
																	borderBottom: `1px solid ${c.divider}`,
																	// Selected row is clearly filled (incl. negatives);
																	// positives keep their red tint.
																	bgcolor: a.positive
																		? c.posBg
																		: i === analyteIdx
																			? c.band
																			: "transparent",
																	cursor: "pointer",
																	boxShadow:
																		i === analyteIdx
																			? `inset 3px 0 0 ${c.accent}`
																			: "none",
																	"&:hover": {
																		bgcolor: a.positive ? c.posBg : c.band,
																	},
																}}
															>
																<Box
																	component="td"
																	sx={{
																		p: "18px 24px",
																		fontWeight:
																			a.positive || i === analyteIdx
																				? 700
																				: 400,
																	}}
																>
																	{a.shortName ?? "—"}
																</Box>
																<Box
																	component="td"
																	sx={{
																		p: "18px 24px",
																		fontWeight: a.positive ? 700 : 400,
																	}}
																>
																	{a.scientificName ?? "—"}
																</Box>
																<Box
																	component="td"
																	sx={{ p: "18px 24px", textAlign: "right" }}
																>
																	<Box
																		component="span"
																		sx={{
																			fontSize: 13,
																			fontWeight: 700,
																			letterSpacing: "0.04em",
																			color: a.positive ? c.posInk : c.negInk,
																		}}
																	>
																		{a.positive ? "POSITIVE" : "NEGATIVE"}
																	</Box>
																</Box>
															</Box>
														))
													)}
												</Box>
											</Box>
										</Box>
									</Paper>
								</Stack>
							)}
						</Box>
					)}

					<Footnote />

					<Stack
						direction="row"
						justifyContent="space-between"
						alignItems="center"
						sx={{ pt: 1.5, borderTop: `1px solid ${c.border}` }}
					>
						<Typography sx={{ fontSize: 11, color: c.muted }}>
							{[programName, brand?.title].filter(Boolean).join(" — ")}
						</Typography>
						<Typography
							sx={{
								fontSize: 11,
								fontWeight: 700,
								color: c.bandDark,
								letterSpacing: "0.08em",
							}}
						>
							SIMS
						</Typography>
						<Typography sx={{ fontSize: 11, color: c.muted }}>
							Generated {generated}
						</Typography>
					</Stack>
				</>
			)}
		</Stack>
	);
};

export default VisitDetailTab;
